import { CalendarAppointmentPatient, calendarAppointments, CalendarAppointmentsOutput, LegacyUICalendarEventAppointment } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { CamelCaseKeys, camelcaseKeys, SessionState } from '@uplift/types';
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

export type TodaysAppointmentWithPatient = {
  appointment: Omit<CamelCaseKeys<LegacyUICalendarEventAppointment>, 'patient'>;
  patient: CamelCaseKeys<CalendarAppointmentPatient>;
};

const convertCalendarAppointmentsResponse = (response: CalendarAppointmentsOutput) => {
  if (response?.appointments && response?.patients) {
    const appointments = camelcaseKeys(response.appointments);

    return appointments.map((appointment): TodaysAppointmentWithPatient => {
      return {
        appointment,
        patient: camelcaseKeys(response.patients[appointment.patientId]),
      };
    });
  }

  return [];
};

const appointmentStates: SessionState[] = [
  'initial',
  'scheduled',
  'started'
];

export const useTodaysAppointments = () => {
  const timezone = useTimezone();

  const [ prevDate, nextDate ] = useMemo(() => {
    const now = CalendarDateService.now(timezone);
    const prevDate = now.subtract(1, 'hour');
    const nextDate = now.endOf('day');

    return [ prevDate, nextDate ];
  }, [ timezone ]);

  const { data: todaysAppointments = [], isLoading: isTodaysAppointmentsLoading, refetch: refetchTodaysAppointments } = useQuery(
    [ 'todays-appointments', prevDate, nextDate ],
    async () => {
      const response = await calendarAppointments({ datetime_from: prevDate.format(), datetime_to: nextDate.format(), states: appointmentStates });

      return convertCalendarAppointmentsResponse(response);
    }
  );
  const { data: nextAppointment = null, isInitialLoading: isNextAppointmentsLoading } = useQuery(
    [ 'next-appointment' ],
    async () => {
      const response = await calendarAppointments({ limit: 1, states: appointmentStates });

      return convertCalendarAppointmentsResponse(response)?.[0];
    },
    {
      enabled: !isTodaysAppointmentsLoading && !todaysAppointments.length
    }
  );

  const refetchOnUpdate = useCallback(() => {
    refetchTodaysAppointments();
  }, [ refetchTodaysAppointments ]);

  return {
    todaysAppointments: todaysAppointments.length ? todaysAppointments : null,
    nextAppointment,
    fetching: isTodaysAppointmentsLoading || isNextAppointmentsLoading,
    refetchOnUpdate
  };
};
